import {
    AccountFactory,
    ActivityFactory,
    AlbumFactory,
    AudioFactory,
    BannedIpsFactory,
    BookmarkFactory,
    CommentFactory,
    EmailDomainWhitelistFactory,
    FeedbackFactory,
    FeedFactory,
    GeoFactory,
    GroupFactory,
    LikeFactory,
    MessageFactory,
    ModAdminFactory,
    ModEntityFactory,
    ModerationActionFactory,
    ModerationWarningFactory,
    ModeratorFactory,
    ModLogsFactory,
    NotificationFactory,
    OAuthFactory,
    PhotoFactory,
    PostFactory,
    ProfileFactory,
    RelationFactory,
    ReportFactory,
    SearchFactory,
    SettingsFactory,
    TextFiltersFactory,
    ThreadFactory,
    TokenFactory,
    TotalFactory,
    TripFactory,
    TripsFactory,
    UserAboutFactory,
    UserCountersFactory,
    UserFactory,
    UserMatchSettingsFactory,
    UserStatusFactory,
    UserTagFactory,
    UserThreadFactory,
    UserWarningFactory,
    ValidationError,
    ViewsFactory,
    ViewsStatsFactory,
} from '@interpals/api-web';
import i18n, { TFunction } from 'i18next';
import differenceInSeconds from 'date-fns/differenceInSeconds';

import { captureException } from 'utils/errorHandling';
import { createCustomToast } from 'services/toasts';
import { logout } from 'redux/auth/actions';
import store from 'redux/redux-store';
import { setLastConnectionErrorTime, setRateLimitLastRequestTime } from 'redux/rate-limit/actions';
import { DEFAULT_RATE_LIMIT_TTL } from 'constants/time';

const configuration = {
    apiKey: () => store.getState().auth.token,
};

export const interpalsApiUrl = process.env.REACT_APP_API_BASE_PATH ?? 'https://api.interpals.net/v1';
if (interpalsApiUrl === '') {
    throw new Error('REACT_APP_API_BASE_PATH is not defined');
}

export type InterpalsWebApi = ReturnType<typeof AccountFactory> &
    ReturnType<typeof ActivityFactory> &
    ReturnType<typeof AlbumFactory> &
    ReturnType<typeof AudioFactory> &
    ReturnType<typeof BannedIpsFactory> &
    ReturnType<typeof BookmarkFactory> &
    ReturnType<typeof CommentFactory> &
    ReturnType<typeof FeedFactory> &
    ReturnType<typeof FeedbackFactory> &
    ReturnType<typeof GeoFactory> &
    ReturnType<typeof GroupFactory> &
    ReturnType<typeof LikeFactory> &
    ReturnType<typeof MessageFactory> &
    ReturnType<typeof ModEntityFactory> &
    ReturnType<typeof ModLogsFactory> &
    ReturnType<typeof ModerationWarningFactory> &
    ReturnType<typeof BannedIpsFactory> &
    ReturnType<typeof ModerationActionFactory> &
    ReturnType<typeof ModeratorFactory> &
    ReturnType<typeof NotificationFactory> &
    ReturnType<typeof OAuthFactory> &
    ReturnType<typeof PhotoFactory> &
    ReturnType<typeof PostFactory> &
    ReturnType<typeof ProfileFactory> &
    ReturnType<typeof RelationFactory> &
    ReturnType<typeof ReportFactory> &
    ReturnType<typeof SearchFactory> &
    ReturnType<typeof SettingsFactory> &
    ReturnType<typeof TextFiltersFactory> &
    ReturnType<typeof ThreadFactory> &
    ReturnType<typeof TokenFactory> &
    ReturnType<typeof TotalFactory> &
    ReturnType<typeof TripFactory> &
    ReturnType<typeof TripsFactory> &
    ReturnType<typeof UserAboutFactory> &
    ReturnType<typeof UserCountersFactory> &
    ReturnType<typeof UserFactory> &
    ReturnType<typeof UserMatchSettingsFactory> &
    ReturnType<typeof UserStatusFactory> &
    ReturnType<typeof UserTagFactory> &
    ReturnType<typeof UserThreadFactory> &
    ReturnType<typeof UserWarningFactory> &
    ReturnType<typeof ViewsFactory> &
    ReturnType<typeof ViewsStatsFactory> &
    ReturnType<typeof EmailDomainWhitelistFactory> &
    ReturnType<typeof ModAdminFactory>;

const api: InterpalsWebApi = {
    ...AccountFactory(configuration),
    ...ActivityFactory(configuration),
    ...AlbumFactory(configuration),
    ...AudioFactory(configuration),
    ...BannedIpsFactory(configuration),
    ...BookmarkFactory(configuration),
    ...CommentFactory(configuration),
    ...FeedFactory(configuration),
    ...FeedbackFactory(configuration),
    ...GeoFactory(configuration),
    ...GroupFactory(configuration),
    ...LikeFactory(configuration),
    ...MessageFactory(configuration),
    ...ModEntityFactory(configuration),
    ...ModLogsFactory(configuration),
    ...ModerationWarningFactory(configuration),
    ...BannedIpsFactory(configuration),
    ...ModeratorFactory(configuration),
    ...ModerationActionFactory(configuration),
    ...NotificationFactory(configuration),
    ...OAuthFactory(configuration),
    ...PhotoFactory(configuration),
    ...PostFactory(configuration),
    ...ProfileFactory(configuration),
    ...RelationFactory(configuration),
    ...ReportFactory(configuration),
    ...SearchFactory(configuration),
    ...SettingsFactory(configuration),
    ...TextFiltersFactory(configuration),
    ...ThreadFactory(configuration),
    ...TokenFactory(configuration),
    ...TotalFactory(configuration),
    ...TripFactory(configuration),
    ...TripsFactory(configuration),
    ...UserAboutFactory(configuration),
    ...UserCountersFactory(configuration),
    ...UserFactory(configuration),
    ...UserMatchSettingsFactory(configuration),
    ...UserStatusFactory(configuration),
    ...UserTagFactory(configuration),
    ...UserThreadFactory(configuration),
    ...UserWarningFactory(configuration),
    ...ViewsFactory(configuration),
    ...ViewsStatsFactory(configuration),
    ...EmailDomainWhitelistFactory(configuration),
    ...ModAdminFactory(configuration),
};

// Array of error codes to ignore
const unhandledExceptions: string[] = ['thread'];

const handleReject = async (response: Response | TypeError | Error) => {
    if (response instanceof TypeError) {
        const errMsg = response.message === 'Failed to fetch' ? i18n.t('ErrorInfo.ConnectionError') : response.message;
        const currentTime = new Date();
        const lastRequestTime = store.getState().rateLimit.lastConnectionErrorTime;
        if (differenceInSeconds(currentTime, lastRequestTime || 0) >= DEFAULT_RATE_LIMIT_TTL) {
            createCustomToast('error', errMsg);
        }
        response.message === 'Failed to fetch' && store.dispatch(setLastConnectionErrorTime(new Date().getTime()));

        throw response;
    }

    if (!(response instanceof Response)) {
        throw response;
    }

    const responseClone = response.clone();
    const error = await (async () => {
        try {
            return (await responseClone.json())?.error;
        } catch (e) {
            throw new Error(e as string);
        }
    })();

    if (error && unhandledExceptions.some((item) => item === error.code)) throw response;

    if (response.status === 401) {
        console.log('Received 401 from server, logging out');
        store.dispatch(logout());
    } else if (response.status === 404) {
        // All 404 errors should be handled at the component level
    } else if (response.status === 400) {
        // Validation exceptions must be handled at the component/action level, since they
        // refer to specific parameters and the error messages should appear next to the fields
    } else if (response.status === 500) {
        createCustomToast('error', `${i18n.t('ErrorInfo.AdviceError500')}`);
    } else if (error) {
        const errMsg = error.message ?? `${error.message}`;
        const currentTime = new Date();
        const lastRequestTime = store.getState().rateLimit.lastRequestTime;
        if (differenceInSeconds(currentTime, lastRequestTime || 0) >= DEFAULT_RATE_LIMIT_TTL) {
            createCustomToast('error', errMsg);
        }
        error.code === 'rate_limit_exceeded' && store.dispatch(setRateLimitLastRequestTime(new Date().getTime()));
    }

    throw response;
};

type ApiMethod = keyof typeof api;

(Object.keys(api) as ApiMethod[]).forEach((method: ApiMethod) => {
    const originalMethod = api[method];

    api[method] = async (...args: Parameters<typeof originalMethod>) => {
        try {
            // @ts-ignore
            return await originalMethod.apply(api, args);
        } catch (err) {
            await handleReject(err as Response);
            await captureException(err as Response, ...args, method);
        }
    };
});

// Transform to object of { param1: errMsg1, ..., paramN: errMsgN }
export const transformValidationErrorMsgs = (validationError: ValidationError) => {
    const errors = validationError.error.errors;

    return Object.keys(errors).reduce(
        (paramsToErrMsgs, param) => {
            paramsToErrMsgs[param] = errors[param].message;

            return paramsToErrMsgs;
        },
        {} as { [key: string]: string },
    );
};

export const showApiErrorToast = async (err: unknown, t: TFunction) => {
    // 400 & 404 errors are handled at the component level, others are shown as toasts
    // above in handleReject before bubbling up
    if (isHandledAtComponentLevel(err)) return;

    const errMsg = await getErrorMessage(err, t);
    createCustomToast('error', errMsg);
};

const isHandledAtComponentLevel = (err: unknown): boolean => {
    return err instanceof Response && (err.status === 400 || err.status === 404);
};

const getErrorMessage = async (err: unknown, t: TFunction): Promise<string> => {
    if (err instanceof Response) {
        const jsonResponse = await err.json();

        return jsonResponse?.error?.message || t('ErrorInfo.UnknownError');
    }

    if (err && typeof err === 'object' && 'message' in err) {
        return (err as { message: string }).message;
    }

    return t('ErrorInfo.UnknownError');
};

export default api;
